import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { LuAlertCircle, LuChevronLeft, LuChevronRight, LuDownload } from "react-icons/lu";
import { saveAs } from "file-saver";
import { PageContainer, PageHeader, ContentCard } from "../../../../components/layout";
import Button from "../../../../components/common/Button";
import DatePicker from "../../../../components/DatePicker";
import Spinner from "../../../../components/Spinner";
import { SegmentedControl } from "@/components/ui/segmented-control";
import { translate, getUser } from "../../../../utils/Common";
import { verifyAccessViewPermissions } from "../../../../config/permissions";
import { useIncidents } from "../../../../hooks/useIncidents";
import IncidentKpiRow from "../../Insights/CounterInsights/Incidents/IncidentKpiRow";
import IncidentClosedByUser from "../../Insights/CounterInsights/Incidents/IncidentClosedByUser";
import { MAX_RANGE_DAYS, formatRange, toApiDate, fromApiDate } from "../../Insights/CounterInsights/Incidents/incidentPeriodPresets";
import {
  REPORT_MODES,
  canGoForward,
  daysBetween,
  defaultAnchor,
  formatMonth,
  getIsoWeek,
  getModeRange,
  shiftAnchor,
} from "./reportPeriods";
import {
  Card,
  ReportCatalogTable,
  ReportFlow,
  ReportHighlights,
  ReportIncidentTable,
  ReportOrigins,
  ReportTrend,
  SectionTitle,
  typeName,
} from "./ReportSections";

const t = (key) => translate(`dashboard.incidents.report.${key}`);
const PERMISSIONS_PATH = "/dashboard/incidents";
const COUNTRY_NAMES = { pa: "Panamá", us: "United States", br: "Brasil" };

const IncidentReport = () => {
  const history = useHistory();
  const { getIncidentReport } = useIncidents();
  const canSearch = verifyAccessViewPermissions(["search"], { path: PERMISSIONS_PATH });

  const [mode, setMode] = useState("week");
  const [anchor, setAnchor] = useState(() => defaultAnchor("week"));
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const range = useMemo(() => {
    if (mode !== "custom") return getModeRange(mode, anchor);
    if (!customStart || !customEnd) return null;
    return { dateFrom: toApiDate(customStart), dateTo: toApiDate(customEnd) };
  }, [mode, anchor, customStart, customEnd]);

  const rangeTooLong = range && daysBetween(range.dateFrom, range.dateTo) > MAX_RANGE_DAYS;

  useEffect(() => {
    if (!range || rangeTooLong || !canSearch) return undefined;
    const abortController = new AbortController();

    const load = async () => {
      setIsLoading(true);
      setHasError(false);
      try {
        setReport(await getIncidentReport(abortController.signal, range));
      } catch (error) {
        if (error?.name === "AbortError") return;
        setReport(null);
        setHasError(true);
      } finally {
        if (!abortController.signal.aborted) setIsLoading(false);
      }
    };

    load();
    return () => abortController.abort();
  }, [range, rangeTooLong, canSearch, getIncidentReport]);

  const handleModeChange = (value) => {
    setMode(value);
    if (value !== "custom") setAnchor(defaultAnchor(value));
  };

  // El PDF se arma con @react-pdf en el navegador y se descarga como archivo. La librería
  // se carga recién al pedir el primer PDF, así no pesa en el resto de BLI.
  const handleDownload = async () => {
    if (!report) return;
    setIsGenerating(true);
    try {
      const [{ pdf }, { default: IncidentReportPdf }] = await Promise.all([
        import("@react-pdf/renderer"),
        import("./pdf/IncidentReportPdf"),
      ]);
      const blob = await pdf(
        <IncidentReportPdf
          report={report}
          periodLabel={periodLabel}
          country={country}
          generatedAt={new Date().toLocaleString()}
          generatedBy={generatedBy}
          trendLabel={trendLabel}
          logoSrc={`${window.location.origin}/img/logoboxitv2.png`}
          iconSrc={`${window.location.origin}/img/logo-b-v2.png`}
        />
      ).toBlob();
      saveAs(blob, `${t("pdf.fileName")}-${report.dateFrom}_${report.dateTo}.pdf`);
    } catch (error) {
      setHasError(true);
    } finally {
      setIsGenerating(false);
    }
  };

  const periodTitle = () => {
    if (!range) return t("pickRange");
    if (mode === "week") {
      const { week } = getIsoWeek(fromApiDate(range.dateFrom));
      return `${t("week")} ${week} · ${formatRange(range.dateFrom, range.dateTo)}`;
    }
    if (mode === "month") return formatMonth(anchor);
    return formatRange(range.dateFrom, range.dateTo);
  };

  const trendLabel = (period) => {
    if (mode === "week") return `S${getIsoWeek(fromApiDate(period.dateFrom)).week}`;
    if (mode === "month") return formatMonth(fromApiDate(period.dateFrom)).slice(0, 3);
    return formatRange(period.dateFrom, period.dateTo).split(" – ")[0];
  };

  let country = "";
  try {
    country = COUNTRY_NAMES[(localStorage.getItem("country") || "").toLowerCase()] || "";
  } catch {
    country = "";
  }
  const user = getUser();
  const generatedBy = [user?.NombreSimple?.split(" ")[0], user?.ApellidoSimple].filter(Boolean).join(" ");
  const periodLabel = periodTitle();

  return (
    <PageContainer>
      <PageHeader
       
        title={t("title")}
        subtitle={t("subtitle")}
        icon={<LuAlertCircle size={28} />}
        breadcrumbs={[
          <span key="list" role="button" tabIndex={0} className="cursor-pointer hover:underline" onClick={() => history.push(PERMISSIONS_PATH)}>
            {translate("global.listIncident")}
          </span>,
          t("title"),
        ]}
        actions={
          <Button variant="primary" size="md" leftIcon={<LuDownload size={18} />} onClick={handleDownload} loading={isGenerating} disabled={!report || isLoading || isGenerating}>
            {isGenerating ? t("generating") : t("downloadPdf")}
          </Button>
        }
      />

      <ContentCard padding="md" className="mb-5">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <SegmentedControl
            name="incidentReportMode"
            label={t("periodType")}
            options={REPORT_MODES.map((value) => ({ value, label: t(`modes.${value}`) }))}
            value={mode}
            onChange={handleModeChange}
          />

          {mode !== "custom" ? (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" aria-label={t("previousPeriod")} onClick={() => setAnchor(shiftAnchor(mode, anchor, -1))}>
                <LuChevronLeft size={16} />
              </Button>
              <span className="min-w-[200px] text-center text-sm font-semibold text-foreground">{periodLabel}</span>
              <Button
                variant="outline"
                size="sm"
                aria-label={t("nextPeriod")}
                onClick={() => setAnchor(shiftAnchor(mode, anchor, 1))}
                disabled={!canGoForward(mode, anchor)}
              >
                <LuChevronRight size={16} />
              </Button>
            </div>
          ) : (
            <div className="grid w-full max-w-md grid-cols-1 gap-x-3 sm:grid-cols-2">
              <DatePicker
                selectedDate={customStart}
                onChangeDate={(date, which) => (which === "end" ? setCustomEnd(null) : setCustomStart(date))}
                placeholder={translate("dashboard.insights.incidents.presets.from")}
                endDate={customEnd}
                maxRangeDays={MAX_RANGE_DAYS - 1}
              />
              <DatePicker
                selectedDate={customEnd}
                onChangeDate={setCustomEnd}
                placeholder={translate("dashboard.insights.incidents.presets.to")}
                startDate={customStart}
                endDate={customEnd}
                disabled={!customStart}
                maxRangeDays={MAX_RANGE_DAYS - 1}
              />
            </div>
          )}
        </div>
      </ContentCard>

      {!canSearch ? (
        <ContentCard padding="md">{translate("global.noDataToView")}</ContentCard>
      ) : !range ? (
        <ContentCard padding="md"><p className="m-0 text-sm text-muted-foreground">{t("pickRange")}</p></ContentCard>
      ) : rangeTooLong ? (
        <ContentCard padding="md"><p className="m-0 text-sm text-muted-foreground">{translate("dashboard.insights.incidents.presets.rangeTooLong")}</p></ContentCard>
      ) : hasError ? (
        <ContentCard padding="md"><p className="m-0 text-sm text-destructive">{t("error")}</p></ContentCard>
      ) : !report || isLoading ? (
        <ContentCard padding="md"><div className="flex justify-center py-10"><Spinner type={1} /></div></ContentCard>
      ) : (
        <div className="flex w-full min-w-0 flex-col gap-5">
          {/* Encabezado del documento: es lo primero que se ve en el PDF */}
          <div className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <img src="/img/logoboxitv2.png" alt="boxit" className="h-8 w-auto" />
              <p className="m-0 text-right text-[11px] leading-relaxed text-muted-foreground">
                {t("generatedAt")} {new Date().toLocaleString()}
                {generatedBy && <><br />{t("generatedBy")} {generatedBy}</>}
              </p>
            </div>
            <div>
              <p className="m-0 bg-gradient-to-r from-[#FF8A65] via-secondary to-primary bg-clip-text text-[11px] font-semibold uppercase tracking-[0.14em] text-transparent">
                {t("title")}
              </p>
              <h2 className="m-0 mt-1 text-2xl font-semibold tracking-tight text-foreground md:text-3xl">{periodLabel}</h2>
              <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-muted-foreground">
                {country && <span className="rounded-full bg-muted px-2.5 py-1">{t("operation")}: {country}</span>}
                <span className="rounded-full bg-muted px-2.5 py-1">
                  {t("comparedWith")} {formatRange(report.previous.dateFrom, report.previous.dateTo)}
                </span>
              </div>
            </div>
          </div>

          <ReportHighlights report={report} />

          <section>
            <SectionTitle title={t("sections.kpis")} />
            <IncidentKpiRow summary={report.summary} previous={report.previous} loading={false} />
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              <div className="flex items-center justify-between rounded-2xl bg-destructive/10 p-4">
                <p className="m-0 text-xs font-semibold text-destructive">{t("cards.over48")}</p>
                <p className="m-0 text-2xl font-semibold text-foreground">{report.over48hCount}</p>
              </div>
              <div className="flex items-center justify-between rounded-2xl bg-warning/10 p-4">
                <p className="m-0 text-xs font-semibold text-warning">{t("cards.pending")}</p>
                <p className="m-0 text-2xl font-semibold text-foreground">{report.pendingFollowUpCount}</p>
              </div>
              <div className="flex items-center justify-between rounded-2xl bg-secondary/10 p-4">
                <div>
                  <p className="m-0 text-xs font-semibold text-secondary">Backlog</p>
                  <p className="m-0 text-[11px] text-muted-foreground">{t("cards.backlogHint")}</p>
                </div>
                <p className="m-0 text-2xl font-semibold text-foreground">{report.flow.backlog}</p>
              </div>
            </div>
          </section>

          <section>
            <SectionTitle title={t("sections.flow")} aside={t("flow.formula")} />
            <ReportFlow flow={report.flow} aging={report.aging} over48hCount={report.over48hCount} />
          </section>

          <section>
            <SectionTitle
              title={t("sections.byType")}
              aside={report.topType ? `${t("topType")}: ${typeName(report.topType)}` : null}
            />
            <Card>
              <ReportCatalogTable rows={report.byType} total={report.summary.created} nameOf={typeName} emptyLabel={t("empty")} />
            </Card>
          </section>

          <section>
            <SectionTitle title={t("sections.byOrigin")} />
            <Card>
              <ReportOrigins rows={report.byOrigin} total={report.summary.created} />
            </Card>
          </section>

          <section>
            <SectionTitle title={t("sections.byUser")} aside={`${report.summary.closed} ${translate("dashboard.insights.incidents.kpi.closed").toLowerCase()}`} />
            <Card>
              {report.byUser.length ? (
                <IncidentClosedByUser users={report.byUser} totalClosed={report.summary.closed} />
              ) : (
                <p className="m-0 text-sm text-muted-foreground">{t("empty")}</p>
              )}
            </Card>
          </section>

          <section>
            <SectionTitle title={`${t("sections.over48")} · ${report.over48h.length}`} aside={t("sortedOldest")} />
            <Card>
              <ReportIncidentTable rows={report.over48h} variant="aged" onOpen={(id) => history.push(`${PERMISSIONS_PATH}/${id}`)} />
            </Card>
          </section>

          <section>
            <SectionTitle title={`${t("sections.pending")} · ${report.pendingFollowUp.length}`} aside={t("pendingRule")} />
            <Card>
              <ReportIncidentTable rows={report.pendingFollowUp} variant="followUp" onOpen={(id) => history.push(`${PERMISSIONS_PATH}/${id}`)} />
            </Card>
          </section>

          <section>
            <SectionTitle title={fill(t("sections.trend"), report.trend.length)} />
            <ReportTrend trend={report.trend} labelOf={trendLabel} />
          </section>

          <p className="m-0 text-[11px] leading-relaxed text-muted-foreground">
            <b>{t("howTitle")}</b> {t("how")}
          </p>
        </div>
      )}
    </PageContainer>
  );
};

const fill = (text, ...values) => values.reduce((acc, value, index) => acc.replace(`{${index}}`, value), text || "");

export default IncidentReport;
